/**
 * What a signed-in person sees when the laboratory cannot hand back their
 * session details.
 *
 * "Could not load your profile" blamed the person's account for what is, most
 * of the time, a dropped connection or a service restarting. The two causes need
 * different words and different next steps, so the screen tells them apart:
 *
 *  - the service did not answer (network failure, 5xx): nothing is wrong with the
 *    account, trying again is the right move;
 *  - the service answered and refused (401, 403, 404): the account is not fully
 *    set up, and only an administrator can fix that.
 */

import { CheckCircle2, CircleDashed, LogOut, RefreshCw, ShieldCheck, WifiOff } from "lucide-react";

import { ApiError } from "@/lib/api";

type Props = {
  error: unknown;
  retrying: boolean;
  onRetry: () => void;
  onSignOut: () => void;
  email?: string | null;
};

export function SessionProblem({ error, retrying, onRetry, onSignOut, email }: Props) {
  const refused =
    error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 404);

  const Icon = refused ? ShieldCheck : WifiOff;
  const title = refused ? "Your workspace is not ready yet" : "We cannot reach the laboratory";
  const lead = refused
    ? "You are signed in, but your account has not been given a role and department. An administrator needs to finish setting it up before you can start working."
    : "Your account is fine. The laboratory service did not answer, which is usually a brief connection problem or a restart in progress.";

  const steps = refused
    ? [
        { done: true, text: "Signed in" },
        { done: false, text: "Role and department assigned by an administrator" },
        { done: false, text: "Workspace opens" },
      ]
    : [
        { done: true, text: "Signed in" },
        { done: false, text: "Connecting to the laboratory" },
        { done: false, text: "Workspace opens" },
      ];

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="w-full max-w-lg">
        <div className="panel overflow-hidden">
          <div className="h-1 bg-brand" />
          <div className="p-8">
            <span className="grid size-12 place-items-center rounded-xl bg-brand/10 text-brand">
              <Icon size={24} />
            </span>
            <h1 className="mt-5 text-xl font-semibold tracking-tight">{title}</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{lead}</p>

            <ol className="mt-6 space-y-3 border-l border-border pl-5">
              {steps.map((step) => (
                <li key={step.text} className="relative flex items-center gap-2.5 text-sm">
                  <span className="absolute -left-[31px] grid size-5 place-items-center rounded-full bg-background">
                    {step.done ? (
                      <CheckCircle2 size={18} className="text-brand" />
                    ) : (
                      <CircleDashed size={18} className="text-muted-foreground" />
                    )}
                  </span>
                  <span className={step.done ? "font-medium" : "text-muted-foreground"}>{step.text}</span>
                </li>
              ))}
            </ol>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <button className="btn btn-primary" onClick={onRetry} disabled={retrying}>
                <RefreshCw size={15} className={retrying ? "animate-spin" : undefined} />
                {retrying ? "Checking" : "Try again"}
              </button>
              <button className="btn btn-outline" onClick={onSignOut}>
                <LogOut size={15} /> Sign out
              </button>
            </div>
          </div>
          {email ? (
            <div className="border-t border-border bg-muted/40 px-8 py-3 text-xs text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{email}</span>. Not you? Sign out and
              use a different account.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
